import { Box, Button, Card, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material'
import React, { useState } from 'react'
import { MoreInfoModalTabs } from '../../types'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Properties } from '../../models'
import { FileButton } from '@e3dc-react/shell/Elements/Buttons'
import { MimeType } from '@e3dc-react/shell/Types'
import { Documents } from '../../models/power-meter.model'
import { J1ToprightarrowC, K1World } from '@e3dc-react/icons'
import useIsWebviewMode from 'src/Hooks/use-is-webview-mode'

const DEFAULT_MIME_TYPE: MimeType = 'application/pdf'

interface MoreInfoProductCategoriesProps {
  productDescription: string | undefined
  width: number | null
  height: number | null
  length: number | null
  weight: number | null
  properties: Properties[]
  documents: Record<string, Documents>
}

const useStyles = makeStyles()(theme => ({
  buttonContainer: {
    margin: theme.spacing(2),
    marginLeft: 0,
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      justifyContent: 'center',
    },
    [theme.breakpoints.only('xs')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  button: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.only('xs')]: {
      margin: theme.spacing(1),
    },
  },
  buttonActive: {
    backgroundColor: theme.palette.grey.grey04,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.grey.grey04,
    },
  },
  informationContainer: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  documentsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  downloadIcon: {
    width: 30,
    height: 30,
  },
}))

const MoreInfoProductCategories: React.FC<MoreInfoProductCategoriesProps> = ({
  productDescription,
  width,
  height,
  weight,
  length,
  properties,
  documents,
}) => {
  const [activeTab, setActiveTab] = useState(MoreInfoModalTabs.DESCRIPTION)
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()
  const hasDownloads = Object.keys(documents).length > 0
  const isWebview = useIsWebviewMode()

  const handlePdf = (fileUrl: string): void => {
    window.open(fileUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box>
      <Box className={classes.buttonContainer}>
        <Button
          className={cx(classes.button, { [classes.buttonActive]: activeTab === MoreInfoModalTabs.DESCRIPTION })}
          color="inherit"
          variant="outlined"
          onClick={() => setActiveTab(MoreInfoModalTabs.DESCRIPTION)}
        >
          {t('shop.product.description')}
        </Button>

        {hasDownloads && (
          <Button
            className={cx(classes.button, { [classes.buttonActive]: activeTab === MoreInfoModalTabs.DOWNLOADS })}
            color="inherit"
            variant="outlined"
            onClick={() => setActiveTab(MoreInfoModalTabs.DOWNLOADS)}
          >
            {t('shop.product.downloads')}
          </Button>
        )}
      </Box>

      {activeTab === MoreInfoModalTabs.DESCRIPTION && (
        <Card className={classes.informationContainer}>
          <Typography variant="h5">{productDescription && <div dangerouslySetInnerHTML={{ __html: productDescription }} />}</Typography>
        </Card>
      )}
      {activeTab === MoreInfoModalTabs.TECHNICHAL_INFORMATION && (
        <Card className={classes.informationContainer}>
          <Table size="small">
            <TableBody>
              {width !== null && (
                <TableRow>
                  <TableCell>{t('shop.product.width')}</TableCell>
                  <TableCell>{width}</TableCell>
                </TableRow>
              )}
              {height !== null && (
                <TableRow>
                  <TableCell>{t('shop.product.height')}</TableCell>
                  <TableCell>{height}</TableCell>
                </TableRow>
              )}
              {length !== null && (
                <TableRow>
                  <TableCell>{t('shop.product.length')}</TableCell>
                  <TableCell>{length}</TableCell>
                </TableRow>
              )}
              {weight !== null && (
                <TableRow>
                  <TableCell>{t('shop.product.weight')}</TableCell>
                  <TableCell>{weight}</TableCell>
                </TableRow>
              )}
              {properties.length > 0 &&
                properties.map((property, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell>{property.group.translated.name}</TableCell>
                      <TableCell>{property.translated.name}</TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </Card>
      )}
      {activeTab === MoreInfoModalTabs.DOWNLOADS && (
        <Card className={classes.informationContainer}>
          <Box className={classes.documentsWrapper}>
            {Object.values(documents)?.map(document => {
              const documentUrl = document?.url
              const fileName = document?.label ?? ''
              const isMedia = Boolean(document.mediaId)

              return (
                documentUrl && (
                  <FileButton
                    key={documentUrl}
                    imageUrl={isMedia ? undefined : <K1World color="primary" className={classes.downloadIcon} />}
                    actionIcon={isMedia ? undefined : <J1ToprightarrowC />}
                    name={fileName}
                    ext={''}
                    hideExtension
                    additionalContent
                    onClickDownload={() => handlePdf(documentUrl)}
                    apiEndpoint={process.env.REACT_APP_API_ENDPOINT ?? ''}
                    mimeType={DEFAULT_MIME_TYPE}
                    isWebview={isWebview}
                  />
                )
              )
            })}
          </Box>
        </Card>
      )}
    </Box>
  )
}

export default MoreInfoProductCategories
